import type { AxiosInstance } from 'axios'
import { EMethod } from '@bc-library/interface'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'salesDueDiligences'

export type DueDiligenceStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'enriching'
  | 'completed'
  | 'failed'

/** Internal data the user agreed to send to the AI (the company name / location always is). */
export type OrbitAiSharing = {
  projects: boolean
  deal: boolean
  evaluations: boolean
}

export type SalesDueDiligence = {
  _id: string
  companyId: string
  engagementId?: string | null
  language?: string
  /** Absent on analyses created before the choice existed. */
  aiSharing?: OrbitAiSharing
  /** Who launched it (analyses are shared with the whole firm). */
  authorName?: string | null
  /** Next possible relaunch for this account (null when failed). */
  refreshAvailableAt?: string | null
  status: DueDiligenceStatus
  error?: string | null
  briefMarkdown?: string | null
  risks?: string[]
  questionsToAsk?: string[]
  companySnapshot?: {
    display?: string
    city?: string
    country?: string
  }
  internalContext?: {
    deliveryStats?: {
      soldTotal?: number
      openDeals?: number
      soldThisYear?: number
      soldLastYear?: number
      soldThisYearProjects?: Array<{ _id: string; display?: string }>
      soldLastYearProjects?: Array<{ _id: string; display?: string }>
      recentSold?: Array<{ _id: string; display?: string }>
      recentOpen?: Array<{ _id: string; display?: string }>
    }
    team?: Array<{
      name?: string
      roles?: string[]
      projectCount?: number
      recentProjects?: Array<string | { _id?: string; display?: string }>
    }>
    relationshipSignals?: {
      period?: { from?: string; to?: string; yearsBack?: number } | null
      summary?: {
        evaluationCount?: number
        positiveCount?: number
        negativeCount?: number
        mixedCount?: number
        watchoutCount?: number
        averageScore?: number | null
      } | null
      positive?: Array<{
        projectId?: string | null
        projectDisplay?: string | null
        evaluateeName?: string | null
        year?: number | null
        outcome?: string
        hint?: string
        askForDetails?: string[]
        globalAverage?: number | null
        commentPreview?: string | null
      }>
      watchouts?: Array<{
        projectId?: string | null
        projectDisplay?: string | null
        evaluateeName?: string | null
        year?: number | null
        outcome?: string
        hint?: string
        askForDetails?: string[]
        globalAverage?: number | null
        commentPreview?: string | null
      }>
    }
  }
  deepResearch?: {
    markdown?: string | null
    sources?: Array<{ title?: string | null; url?: string; domain?: string | null }>
  }
  webEnrichment?: {
    financialsMarkdown?: string | null
    news?: string[]
    strengths?: string[]
    weaknesses?: string[]
    entryContacts?: Array<{
      name?: string | null
      role?: string
      company?: string | null
      rationale?: string
      sourceUrl?: string | null
      email?: string | null
      phone?: string | null
      linkedinUrl?: string | null
      otherContactUrls?: string[]
      careerSummary?: string | null
      recentActivities?: string[]
    }>
    projectNewsBridges?: Array<{
      projectId?: string | null
      projectDisplay?: string
      newsHeadline?: string
      linkType?: 'reuse' | 'adjacent' | 'risk' | 'opportunity'
      rationale?: string
    }>
    sources?: Array<{ title?: string | null; url?: string; domain?: string | null }>
  }
  relationshipInsightsLevel?: 'surface' | 'detailed'
  evaluationInsights?: {
    status?: 'pending' | 'completed' | 'skipped' | 'failed' | null
    recap?: string | null
    importantNotes?: Array<{
      note?: string
      tone?: 'positive' | 'watchout' | 'neutral'
      projectDisplay?: string | null
      people?: string[]
      scoresMentioned?: string[]
    }>
    completedAt?: string
    /** Summary withheld: the viewer lacks the detailed relationship view. */
    restricted?: boolean
  } | null
  progress?: {
    interactionStatus?: string | null
    thoughts?: Array<{ at?: string; text?: string }>
    updatedAt?: string
  }
  queuedAt?: string
  startedAt?: string
  completedAt?: string
  _created?: string
}

const getLatest =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (params: { companyId: string; engagementId?: string | null }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
      query: {
        companyId: params.companyId,
        ...(params.engagementId ? { engagementId: params.engagementId } : {}),
      },
    })
  }

const getById =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (id: string) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    })
  }

const create =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (data: {
    companyId: string
    engagementId?: string | null
    language?: string
    force?: boolean
    aiSharing?: OrbitAiSharing
  }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
      data,
    })
  }

const refresh =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (id: string, data?: { language?: string; aiSharing?: OrbitAiSharing }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/refresh`,
      data: data || {},
    })
  }

export default (axiosInstance: AxiosInstance) => {
  const axios = useAxios(axiosInstance)
  return {
    getLatest: getLatest(axios),
    getById: getById(axios),
    create: create(axios),
    refresh: refresh(axios),
  }
}
